import React from 'react';
import { useLanguage, useTranslation } from '../context/LanguageContext';
import { Layout } from './layout/Layout';
import { SEOHead } from './SEOHead';
import { useLocalizedSettings } from '../hooks/useLocalizedSettings';
import { HomeIntroSection } from './home/HomeIntroSection';
import { HomeStatsSection } from './home/HomeStatsSection';
import { HomeAboutSection } from './home/HomeAboutSection';
import { HomeServicesSection } from './home/HomeServicesSection';
import { HomeWhyUsSection } from './home/HomeWhyUsSection';
import { HomeCtaSection } from './home/HomeCtaSection';
import { HomeContactSection } from './home/HomeContactSection';
import heroBackground from '../assets/images/background.webp';

const BASE_URL = 'https://udent.com.ua';

const CLINIC_SERVICES = [
    'Терапія / Therapy',
    'Хірургія / Surgery',
    'Ортопедія / Prosthodontics',
    'Ортодонтія / Orthodontics',
    'Гігієна / Dental hygiene',
    'Дитяча стоматологія / Pediatric dentistry',
];

const toE164Phone = (phone = '') => {
    const cleaned = phone.replace(/[^\d+]/g, '');
    return cleaned.startsWith('0') ? `+38${cleaned}` : cleaned;
};

/**
 * Home page — composition of section components.
 *
 * Each section lives in src/components/home/Home*Section.js so this file
 * stays focused on layout + SEO concerns (well under the 150-line guideline
 * from AGENTS.md).
 */
export const Content = () => {
    const { language } = useLanguage();
    const { t } = useTranslation();
    const { settings, s } = useLocalizedSettings();

    // Intent-targeted SEO copy; Google truncates descriptions at ~160 chars.
    const seoTitle = t('seo.homeTitle');
    const seoDescription = t('seo.homeDescription');
    const sameAs = [settings.facebookUrl, settings.instagramUrl].filter(Boolean);
    const canonicalUrl = language === 'en' ? `${BASE_URL}/en` : `${BASE_URL}/`;
    const phone = toE164Phone(settings.phone);
    const mapQuery = encodeURIComponent(settings.mapQuery || s('address'));

    return (
        <Layout showAdmin>
            <SEOHead
                title={seoTitle}
                description={seoDescription}
                path="/"
                preloadImage={heroBackground}
                schema={[
                    {
                        '@type': 'Dentist',
                        '@id': `${BASE_URL}/#dentist`,
                        name: 'UDent',
                        description: seoDescription,
                        url: BASE_URL,
                        telephone: phone,
                        email: settings.email,
                        image: `${BASE_URL}/logo512.png`,
                        logo: `${BASE_URL}/logo512.png`,
                        priceRange: '₴₴',
                        currenciesAccepted: 'UAH',
                        paymentAccepted: 'Cash, Credit Card',
                        medicalSpecialty: [
                            'Dentistry',
                            'OrthodonticDentistry',
                            'PediatricDentistry',
                        ],
                        availableService: CLINIC_SERVICES.map((name) => ({
                            '@type': 'MedicalProcedure',
                            name,
                        })),
                        address: {
                            '@type': 'PostalAddress',
                            streetAddress: 'вул. Красівського 1',
                            addressLocality: 'Івано-Франківськ',
                            addressRegion: 'Івано-Франківська область',
                            postalCode: '76000',
                            addressCountry: 'UA',
                        },
                        geo: {
                            '@type': 'GeoCoordinates',
                            latitude: 48.9226,
                            longitude: 24.7103,
                        },
                        areaServed: {
                            '@type': 'City',
                            name: 'Івано-Франківськ',
                        },
                        openingHoursSpecification: [
                            {
                                '@type': 'OpeningHoursSpecification',
                                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                                opens: '09:00',
                                closes: '19:00',
                            },
                            {
                                '@type': 'OpeningHoursSpecification',
                                dayOfWeek: 'Saturday',
                                opens: '10:00',
                                closes: '15:00',
                            },
                        ],
                        hasMap: `https://www.google.com/maps/search/?api=1&query=${mapQuery}`,
                        ...(sameAs.length ? { sameAs } : {}),
                    },
                    {
                        '@type': 'WebSite',
                        '@id': `${BASE_URL}/#website`,
                        name: 'UDent',
                        url: BASE_URL,
                        inLanguage: ['uk', 'en'],
                        publisher: { '@id': `${BASE_URL}/#dentist` },
                    },
                    {
                        '@type': 'WebPage',
                        '@id': `${canonicalUrl}#webpage`,
                        name: seoTitle,
                        description: seoDescription,
                        url: canonicalUrl,
                        isPartOf: { '@id': `${BASE_URL}/#website` },
                        about: { '@id': `${BASE_URL}/#dentist` },
                        inLanguage: language === 'en' ? 'en' : 'uk',
                    },
                ]}
            />

            <HomeIntroSection />
            <HomeStatsSection />
            <HomeAboutSection />
            <HomeServicesSection />
            <HomeWhyUsSection />
            <HomeCtaSection />
            <HomeContactSection />
        </Layout>
    );
};
